import { ErrorCodes } from '@constants';
import axios from 'axios';

import { expireGuestSession, getGuestToken, setGuestSession } from './guest-session';

export const GUEST_TOKEN_HEADER = 'x-orderprotection-guest-token';

let authTokenProvider: (() => string | null | undefined) | null = null;

/**
 * Registered by the app rather than read from a store here: Valet keeps its token in an auth store
 * that Concierge does not have, so this library cannot reach for one. Mirrors
 * setGuestSessionExpiredHandler.
 *
 * Valet reaches the gateway directly, so without this its calls through these services arrive with no
 * credential at all — no bearer token, and none of the front-door header Concierge's proxy stamps.
 * Media signing is the case that surfaced it. Concierge registers nothing and is unaffected.
 */
export const setAuthTokenProvider = (provider: (() => string | null | undefined) | null): void => {
  authTokenProvider = provider;
};

export default {
  create(config = {}) {
    const httpClient = axios.create(config);

    // Attached here rather than in each service so every order-scoped call carries the session
    // without callers having to remember it. Requests made before lookup simply send no header, and
    // the backend guards stay inert until the rollout flag leaves `off`.
    httpClient.interceptors.request.use(request => {
      const token = getGuestToken();
      if (token) request.headers[GUEST_TOKEN_HEADER] = token;

      // Never overwrites an explicit header: a caller that passed its own Authorization means it.
      const authToken = authTokenProvider?.();
      if (authToken && !request.headers.Authorization) request.headers.Authorization = `Bearer ${authToken}`;

      return request;
    });

    httpClient.interceptors.response.use(
      response => {
        setGuestSession(response.data?.guestSession);

        return response;
      },
      error => {
        // Two reasons a session stops being usable, both ending the same way — clear it and let the
        // app send the customer back through lookup: it aged out, or it is valid but belongs to a
        // different order than the page being viewed (an old bookmark, a link opened after looking up
        // something else). Without the second case that customer gets a blank page instead of being
        // asked for their order number.
        //
        // Matched on codes, never on the status: a front-door or entitlements refusal is also a 403,
        // and returns on every request when a deployment is misconfigured — clearing the session each
        // time would be an infinite redirect. A tampered or wrong-type token still fails hard.
        //
        // `code` is what OPException actually serializes ({ status, errors, code }); `errorCode` is
        // the shape other services' filters use. Reading both means a route behind a different
        // filter cannot silently stop triggering the flow.
        const { code, errorCode } = error?.response?.data ?? {};
        const ENDS_SESSION = [ErrorCodes.Unauthorized.GUEST_TOKEN_EXPIRED, ErrorCodes.Forbidden.GUEST_ORDER_MISMATCH];

        if (ENDS_SESSION.includes(code) || ENDS_SESSION.includes(errorCode)) {
          expireGuestSession();
        }

        return Promise.reject(error);
      },
    );

    return httpClient;
  },
};
